import { createAsyncThunk } from '@reduxjs/toolkit';
import api from 'src/app/api';
import { buildEncodedQueryString } from 'src/common';
import { ScheduledEvent, ScheduledEventInput, TriggerScheduledEventInput } from 'src/features/reporting/store/types';
import { AfslTemplate, TemplatesRequest, PracticeTemplate, UpdateScheduledEventRequest } from './types';

export enum SchedulerActionTypes {
  CreateScheduledEvent = '@@scheduler/CreateScheduledEvent',
  GetScheduledEventById = '@@scheduler/GetScheduledEventById',
  UpdateScheduledEvent = '@@scheduler/UpdateScheduledEvent',
  TriggerScheduledEvent = '@@scheduler/TriggerScheduledEvent',
  AfslTemplates = '@@bff/GetAfslTemplates',
  PracticeTemplates = '@@bff/GetPracticeTemplates',
}

export enum SchedulerApi {
  CreateScheduledEvent = '/reporting/scheduledevent/CreateScheduledEvent',
  GetScheduledEventById = '/reporting/scheduledevent/GetScheduledEventById',
  UpdateScheduledEvent = '/reporting/scheduledevent/UpdateScheduledEvent',
  TriggerScheduledEvent = '/reporting/scheduledevent/TriggerScheduledEvent',
  AfslTemplates = '/bff/GetAfslTemplates',
  PracticeTemplates = '/bff/GetPracticeTemplates',
}

export const createScheduledEvent = createAsyncThunk<ScheduledEvent, ScheduledEventInput>(
  SchedulerActionTypes.CreateScheduledEvent,
  async (payload: ScheduledEventInput) => {
    const response = await api.post<ScheduledEvent>(SchedulerApi.CreateScheduledEvent, payload);
    return Promise.resolve<ScheduledEvent>(response.data);
  }
);

export const triggerScheduledEvent = createAsyncThunk(
  SchedulerActionTypes.TriggerScheduledEvent,
  async (payload: TriggerScheduledEventInput) => {
    await api.post(`${SchedulerApi.TriggerScheduledEvent}/${payload.id}`);
  }
);

export const updateScheduledEvent = createAsyncThunk<ScheduledEvent, UpdateScheduledEventRequest>(
  SchedulerActionTypes.UpdateScheduledEvent,
  async (payload: UpdateScheduledEventRequest) => {
    const queryParams = buildEncodedQueryString({
      id: payload.id,
    });
    const response = await api.put<ScheduledEvent>(`${SchedulerApi.UpdateScheduledEvent}${queryParams}`, payload.input);
    return Promise.resolve<ScheduledEvent>(response.data);
  }
);

export const fetchScheduledEventById = createAsyncThunk<ScheduledEvent, string>(
  SchedulerActionTypes.GetScheduledEventById,
  async (id: string) => {
    const queryParams = buildEncodedQueryString({
      id: id,
    });
    const response = await api.get<ScheduledEvent>(`${SchedulerApi.GetScheduledEventById}${queryParams}`);
    return Promise.resolve<ScheduledEvent>(response.data);
  }
);

export const fetchAfslTemplates = createAsyncThunk<AfslTemplate[], TemplatesRequest>(
  SchedulerActionTypes.AfslTemplates,
  async (request: TemplatesRequest) => {
    const queryParams = buildEncodedQueryString({
      entityCoreIds: request.entityCoreIds,
    });
    const response = await api.get<AfslTemplate[]>(`${SchedulerApi.AfslTemplates}${queryParams}`);
    return Promise.resolve<AfslTemplate[]>(response.data);
  }
);

export const fetchPracticeTemplates = createAsyncThunk<PracticeTemplate[], TemplatesRequest>(
  SchedulerActionTypes.PracticeTemplates,
  async (request: TemplatesRequest) => {
    const queryParams = buildEncodedQueryString({
      entityCoreIds: request.entityCoreIds,
    });
    const response = await api.get<PracticeTemplate[]>(`${SchedulerApi.PracticeTemplates}${queryParams}`);
    return Promise.resolve<PracticeTemplate[]>(response.data);
  }
);
